/** Тонкая обёртка над Telegram WebApp + разбор ссылок-приглашений. */

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string };
  viewportStableHeight?: number;
  ready: () => void;
  expand: () => void;
  disableVerticalSwipes?: () => void;
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  /** Bot API 6.4+. Колбэк вызывается с текстом QR; вернуть true — закрыть окно сканера. */
  showScanQrPopup?: (
    params: { text?: string },
    callback?: (data: string) => boolean | void,
  ) => void;
  closeScanQrPopup?: () => void;
  onEvent?: (event: string, handler: (payload?: { isStateStable?: boolean }) => void) => void;
  offEvent?: (event: string, handler: (payload?: { isStateStable?: boolean }) => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(win: Window | undefined = globalThis.window): TelegramWebApp | null {
  return win?.Telegram?.WebApp ?? null;
}

/**
 * initData для авторизации на бэкенде.
 * Вне Telegram работает только в dev-сборке (бэкенд с DEBUG=true): пользователь берётся
 * из `?debug_user=<id>` в адресной строке или из VITE_DEBUG_USER. Так в двух вкладках
 * можно играть за двух разных людей.
 */
export function getInitData(
  win: Window | undefined = globalThis.window,
  env: { DEV: boolean; VITE_DEBUG_USER?: string } = import.meta.env,
): string | null {
  const real = getWebApp(win)?.initData;
  if (real) return real;
  if (!env.DEV) return null;
  const fromUrl = new URLSearchParams(win?.location.search ?? "").get("debug_user");
  const user = fromUrl ?? env.VITE_DEBUG_USER;
  return user && /^\d{1,15}$/.test(user) ? `debug:${user}` : null;
}

const ROOM_RE = /^[A-Z0-9]{6}$/;

function normalizeRoom(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const room = value.trim().toUpperCase();
  return ROOM_RE.test(room) ? room : null;
}

/**
 * Код комнаты из ссылки-приглашения. Источники по приоритету:
 *  1. start_param Telegram: код комнаты (`ABC123`) или старый JSON-формат `{"room":"ABC123","collection":1}`
 *     (в URL-кодировке) — чтобы не сломать ссылки, уже разосланные в чатах;
 *  2. `?room=ABC123` в адресной строке (QR-коды).
 * Признак «комната-коллекция» клиенту больше не нужен: режим приходит от сервера в `state`.
 */
export function resolveRoomId(startParam: string | undefined, search: string): string | null {
  if (startParam) {
    const plain = normalizeRoom(startParam);
    if (plain) return plain;
    try {
      const legacy: unknown = JSON.parse(decodeURIComponent(startParam));
      if (typeof legacy === "object" && legacy !== null && "room" in legacy) {
        const room = normalizeRoom(legacy.room);
        if (room) return room;
      }
    } catch {
      /* не наш формат — идём дальше */
    }
  }
  return normalizeRoom(new URLSearchParams(search).get("room"));
}

/**
 * Код комнаты из того, что ввёл или отсканировал игрок: сам код, ссылка-приглашение
 * (`?startapp=CODE`) или старая ссылка с `?room=CODE`.
 */
export function parseRoomInput(input: string): string | null {
  const text = input.trim();
  const direct = normalizeRoom(text);
  if (direct) return direct;
  try {
    const url = new URL(text);
    return resolveRoomId(url.searchParams.get("startapp") ?? undefined, url.search);
  } catch {
    return null;
  }
}

/** ws(s)://host/ws/ROOM для текущей страницы. */
export function roomSocketUrl(roomId: string, loc: Pick<Location, "protocol" | "host">): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${loc.host}/ws/${encodeURIComponent(roomId)}`;
}
